import { useTranslation as usePublicI18n } from "react-i18next";
import { publicText, publicTemplate } from "./publicText";
import React, { useEffect } from "react";
import { ClockCircleOutlined, ExperimentOutlined, FireOutlined, LineChartOutlined, RadarChartOutlined } from "@ant-design/icons";
import PublicLayout from "./PublicLayout";
import { CASE02 } from "./caseStudyData";
import { Reveal, useDocumentTitle } from "./components/PageUtilities";
import { CaseNavigation, CaseStudyBreadcrumb, DataTable, Figure, KeyFinding, MethodGrid, MetricCard, ScientificNote, SectionHeader, TwoFigureGrid } from "./components/ScientificComponents";
import "./case02.css";
import { trackEvent } from "../FirebaseConfig";
const TONES = ["blue", "green", "orange"];
const TREATMENT_ORDER = ["control", "a", "b"];
function ApertureCards({
  condition,
  dark = false
}) {
  usePublicI18n();
  return <Reveal className={`science-metric-grid is-three case02-aperture-cards ${dark ? "is-dark-grid" : ""}`}>
      {publicText(condition.results.map((item, index) => <MetricCard key={item.key} label={publicText(item.label)} value={item.median.toFixed(2)} unit="µm" caption={publicTemplate(313, [item.openRate.toFixed(1), item.open, item.closed, item.detected])} tone={TONES[index]} icon={<RadarChartOutlined />} />))}
    </Reveal>;
}
const resultColumns = [{
  key: "label",
  label: "処理区"
}, {
  key: "images",
  label: "画像数",
  align: "right"
}, {
  key: "detected",
  label: "検出気孔",
  align: "right"
}, {
  key: "median",
  label: "中央値",
  unit: "µm",
  align: "right",
  render: value => value.toFixed(2)
}, {
  key: "openRate",
  label: "Open率",
  unit: "%",
  align: "right",
  render: value => value.toFixed(1)
}, {
  key: "open",
  label: "Open",
  align: "right"
}, {
  key: "closed",
  label: "Closed",
  align: "right"
}, {
  key: "mean",
  label: "平均",
  unit: "µm",
  align: "right",
  render: value => value.toFixed(2)
}, {
  key: "q1",
  label: "Q1",
  unit: "µm",
  align: "right",
  render: value => value.toFixed(2)
}, {
  key: "q3",
  label: "Q3",
  unit: "µm",
  align: "right",
  render: value => value.toFixed(2)
}];
function ConditionSection({
  condition,
  index,
  eyebrow,
  title,
  description,
  rootState,
  interpretationTitle,
  children,
  dark = false
}) {
  usePublicI18n();
  const className = ["science-section", "case02-condition-section", dark ? "is-dark case02-condition-section--dark" : ""].filter(Boolean).join(" ");
  return <section className={className}>
      <div className="case-container">
        <SectionHeader index={index} eyebrow={eyebrow} title={publicText(title)} description={publicText(description)} />

        <Reveal className="case02-condition-meta">
          <div>
            <ClockCircleOutlined />
            <span>{publicText("\u64AE\u5F71\u30FB\u89E3\u6790\u6642\u9593")}</span>
            <strong>{publicText(condition.window)}</strong>
          </div>
          <div>
            <ExperimentOutlined />
            <span>{publicText("\u6C34\u5206\u6761\u4EF6")}</span>
            <strong>{publicText(rootState)}</strong>
          </div>
          <div>
            <LineChartOutlined />
            <span>{publicText("Kruskal\u2013Wallis")}</span>
            <strong>{publicText(condition.pDisplay)}</strong>
          </div>
        </Reveal>

        <TwoFigureGrid>
          <Figure number={`${index}A`} title={publicTemplate(317, [condition.label])} src={condition.plantImage} alt={publicTemplate(318, [condition.label])} caption={publicText(condition.plantCaption)} className="case02-figure--portrait" />

          <Figure number={`${index}B`} title={publicTemplate(319, [condition.label])} src={condition.analysisImage} alt={publicTemplate(320, [condition.label])} contain caption={publicText(condition.analysisCaption)} className="science-figure--analysis case02-figure--analysis" />
        </TwoFigureGrid>

        <ApertureCards condition={condition} dark={dark} />

        <DataTable caption={publicText(`Table ${index}. ${condition.label}（${condition.window}）`)} columns={resultColumns} rows={condition.results} />

        <KeyFinding title={publicText(interpretationTitle)}>{publicText(children)}</KeyFinding>

        <ScientificNote title={publicTemplate(321, [condition.pDisplay])} tone={condition.significant ? "success" : "info"}>
          <p>{publicText(condition.statisticsNote)}</p>
        </ScientificNote>
      </div>
    </section>;
}
function ConditionDayCards() {
  usePublicI18n();
  return <Reveal className="case02-study-days">
      <article className="case02-study-day is-wet">
        <div className="case02-study-day__header">
          <span>{publicText("2026.07.16")}</span>
          <strong>{publicText("\u6C34\u5206\u304C\u5229\u7528\u3067\u304D\u308B\u6761\u4EF6")}</strong>
        </div>

        <div className="case02-study-day__timeline">
          <div>
            <time>{publicText("\u7D0404:00")}</time>
            <b>{publicText("\u5341\u5206\u306B\u704C\u6C34")}</b>
          </div>
          <i />
          <div>
            <time>{publicText("06:30\u9803")}</time>
            <b>{publicText("\u6E7F\u6F64\u30FB\u958B\u59CB\u70B9")}</b>
          </div>
          <i />
          <div>
            <time>{publicText("\u7D0410:00")}</time>
            <b>{publicText("\u5341\u5206\u306B\u518D\u704C\u6C34")}</b>
          </div>
          <i />
          <div>
            <time>{publicText("11:30\u9803")}</time>
            <b>{publicText("\u6E7F\u6F64\u30FB\u9AD8\u6E29")}</b>
          </div>
        </div>
      </article>

      <article className="case02-study-day is-dry">
        <div className="case02-study-day__header">
          <span>{publicText("2026.07.14")}</span>
          <strong>{publicText("\u57F9\u5730\u4E7E\u71E5\u30FB\u840E\u308C\u6761\u4EF6")}</strong>
        </div>

        <div className="case02-study-day__timeline">
          <div>
            <time>{publicText("\u7D0404:00")}</time>
            <b>{publicText("\u704C\u6C34")}</b>
          </div>
          <i />
          <div className="is-long">
            <time>{publicText("\u305D\u306E\u5F8C")}</time>
            <b>{publicText("\u8FFD\u52A0\u704C\u6C34\u306A\u3057")}</b>
          </div>
          <i />
          <div>
            <time>{publicText("10:58")}</time>
            <b>{publicText("\u840E\u308C\u3092\u64AE\u5F71")}</b>
          </div>
          <i />
          <div>
            <time>{publicText("11:00\u9803")}</time>
            <b>{publicText("\u4E7E\u71E5\u6642\u6E2C\u5B9A")}</b>
          </div>
        </div>
      </article>
    </Reveal>;
}
function ResponseProfile({
  treatment,
  values,
  note,
  highlight = false
}) {
  usePublicI18n();
  const max = 6;
  const getApertureClass = value => {
    if (value < 3.2) return "is-low"; // 青：小さい開度
    if (value < 4.2) return "is-middle"; // 緑：中間
    if (value < 5.0) return "is-high"; // オレンジ：大きめ
    return "is-very-high"; // 赤：かなり大きい
  };

  return <article className={`case02-profile-card treatment-${treatment.key} ${highlight ? "is-highlighted" : ""}`}>
      <div className="case02-profile-card__head">
        <span>{publicText(treatment.label)}</span>

        {publicText(highlight && <b>{publicText("CONDITION-DEPENDENT RESPONSE")}</b>)}
      </div>

      <div className="case02-profile-card__rows">
        {publicText(values.map(item => {
        const apertureClass = getApertureClass(item.value);
        return <div key={item.conditionLabel} className="case02-profile-row">
              <div className="case02-profile-row__content">
                <div className="case02-profile-row__condition">
                  <small>{publicText(item.conditionLabel)}</small>

                  <span>{publicText("Open ")}{publicText(item.openRate.toFixed(1))}{publicText("%")}</span>
                </div>

                <strong className="case02-profile-row__value">
                  {publicText(item.value.toFixed(2))}
                  <span>{publicText(" \xB5m")}</span>
                </strong>
              </div>

              <div className="case02-profile-row__track" aria-hidden="true">
                <span className={apertureClass} style={{
              width: `${Math.min(100, item.value / max * 100)}%`
            }} />
              </div>
            </div>;
      }))}
      </div>

      <p className="case02-profile-card__note">
        {publicText(note)}
      </p>
    </article>;
}
function ApertureLegend() {
  usePublicI18n();
  return <Reveal className="case02-aperture-legend" aria-label={publicText("気孔開度中央値の色分け")}>
      <span><i className="is-low" />{publicText("\u5C0F\u3055\u3044\u958B\u5EA6")}</span>
      <span><i className="is-middle" />{publicText("\u4E2D\u9593")}</span>
      <span><i className="is-high" />{publicText("\u5927\u304D\u3044\u958B\u5EA6")}</span>
      <span><i className="is-very-high" />{publicText("\u975E\u5E38\u306B\u5927\u304D\u3044\u958B\u5EA6")}</span>
      <small>{publicText("\u8272\u306F\u6C17\u5B54\u958B\u5EA6\u4E2D\u592E\u5024\u306E\u5927\u304D\u3055\u3092\u793A\u3057\u3001\u52B9\u679C\u306E\u826F\u5426\u3092\u793A\u3059\u3082\u306E\u3067\u306F\u3042\u308A\u307E\u305B\u3093\u3002")}</small>
    </Reveal>;
}
function DroughtReferenceCard() {
  usePublicI18n();
  return <Reveal className="case02-reference-card">
      <div className="case02-reference-card__label">{publicText("RELATED RESEARCH / DROUGHT RESPONSE")}</div>
      <h3>{publicText("Ethanol-Mediated Novel Survival Strategy against Drought Stress in Plants")}</h3>
      <p>{publicText("\u30A8\u30BF\u30CE\u30FC\u30EB\u524D\u51E6\u7406\u306B\u3088\u308B\u4E7E\u71E5\u8010\u6027\u7814\u7A76\u3067\u306F\u3001ABA\u30B7\u30B0\u30CA\u30EB\u3078\u306E\u95A2\u4E0E\u3001 \u6C17\u5B54\u9589\u9396\u3001\u84B8\u6563\u306E\u4F4E\u4E0B\u3001\u8449\u306E\u6C34\u5206\u4FDD\u6301\u304C\u5831\u544A\u3055\u308C\u3066\u3044\u307E\u3059\u3002")}</p>
      <cite>{publicText("Bashir K, Todaka D, Rasheed S, et al. (2022). Plant and Cell Physiology, 63(9), 1181\u20131192. DOI: 10.1093/pcp/pcac114")}</cite>
      <a href="https://doi.org/10.1093/pcp/pcac114" target="_blank" rel="noreferrer">{publicText("\u8AD6\u6587\u3092\u898B\u308B \u2197")}</a>
    </Reveal>;
}
function PeerReviewedStudyCard() {
  usePublicI18n();
  return <Reveal className="case02-peer-reviewed-paper">
      <div className="case02-peer-reviewed-paper__label">{publicText("STOMATASCOPE IN PEER-REVIEWED RESEARCH")}</div>
      <h3>{publicText("Interactive Effects of Maximum Daytime and Minimum Nighttime Temperatures on Spinach Growth and Physiological Characteristics")}</h3>
      <p>{publicText("StomataScope HQ-1010\u306B\u3088\u308B\u6C17\u5B54\u958B\u5EA6\u3068\u3001\u6C17\u5B54\u30B3\u30F3\u30C0\u30AF\u30BF\u30F3\u30B9\u3001\u84B8\u6563\u901F\u5EA6\u3001 \u8449\u6E29\u3001\u5149\u5316\u5B66\u7CFBII\u306E\u91CF\u5B50\u52B9\u7387\u3092\u540C\u3058\u7814\u7A76\u5185\u3067\u8A55\u4FA1\u3057\u305F\u67FB\u8AAD\u8AD6\u6587\u3067\u3059\u3002")}</p>
      <cite>{publicText("Samba N, Akasaka H, Yasuba K, Goto T, Hikawa-Endo M, Miyama Y. (2026). HortScience 61(2):444\u2013451. DOI: 10.21273/HORTSCI19113-25")}</cite>
      <a href="https://doi.org/10.21273/HORTSCI19113-25" target="_blank" rel="noreferrer">{publicText("\u8AD6\u6587\u3092\u898B\u308B \u2197")}</a>
    </Reveal>;
}
function CaseStudy02() {
  usePublicI18n();
  useDocumentTitle(publicText("Case Study 02｜水分条件によって変わる気孔応答"));
  useEffect(() => {
    trackEvent("case_study_view", {
      case_study_id: "02"
    });
  }, []);
  const wetMorning = CASE02.timepoints.find(item => item.key === "0630");
  const wetLate = CASE02.timepoints.find(item => item.key === "1130");
  const dry = CASE02.relatedObservation;
  const relevantConditions = [wetMorning, wetLate, dry];
  const publicImages = relevantConditions.reduce((sum, condition) => sum + condition.results.reduce((subtotal, item) => subtotal + item.images, 0), 0);
  const publicStomata = relevantConditions.reduce((sum, condition) => sum + condition.results.reduce((subtotal, item) => subtotal + item.detected, 0), 0);
  const profileCards = TREATMENT_ORDER.map(key => {
    const treatment = CASE02.treatments[key];
    const morningResult = wetMorning.results.find(item => item.key === key);
    const lateResult = wetLate.results.find(item => item.key === key);
    const dryResult = dry.results.find(item => item.key === key);
    return {
      key,
      treatment,
      values: [{
        ...morningResult,
        conditionLabel: "6:30頃｜湿潤",
        value: morningResult.median
      }, {
        ...lateResult,
        conditionLabel: "11:30頃｜再灌水後",
        value: lateResult.median
      }, {
        ...dryResult,
        conditionLabel: "7/14 11:00頃｜乾燥・萎れ",
        value: dryResult.median
      }]
    };
  });
  return <PublicLayout>
      <article className="science-case-page case-theme-02 case02-v3">
        <header className="science-case-hero science-case-hero--photo case02-hero case02-hero--strategy">
          <div className="science-case-hero__background-photo">
            <img src={CASE02.images.rewaterPlant} alt={publicText("水分条件比較に用いたトマト苗")} />
            <div />
          </div>

          <div className="case-container">
            <CaseStudyBreadcrumb current="02" />

            <div className="science-case-hero__layout">
              <Reveal className="science-case-hero__copy">
                <div className="science-case-id">{publicText("CASE STUDY 02")}</div>
                <span className="science-case-domain">{publicText("WATER CONDITION \xD7 STOMATAL RESPONSE STRATEGY")}</span>

                <h1>{publicText("\u6C34\u5206\u6761\u4EF6\u306B\u3088\u3063\u3066\u5909\u308F\u308B\u3001")}<br />
                  <em>{publicText("\u6C17\u5B54\u306E\u300C\u4F7F\u3044\u65B9\u300D\u3002")}</em>
                </h1>

                <p>{publicText("\u540C\u3058\u51E6\u7406\u3067\u3082\u3001\u690D\u7269\u306E\u53CD\u5FDC\u306F\u74B0\u5883\u3067\u5909\u308F\u308B\u3002 \u6C34\u5206\u304C\u5229\u7528\u3067\u304D\u308B\u6761\u4EF6\u3068\u3001\u57F9\u5730\u4E7E\u71E5\u30FB\u840E\u308C\u6761\u4EF6\u3067\u3001 Control\u30FBA\u5264\u30FBB\u5264\u306E\u6C17\u5B54\u958B\u5EA6\u4E2D\u592E\u5024\u3092\u6BD4\u8F03\u3057\u307E\u3057\u305F\u3002")}</p>

                <div className="science-case-tags">
                  <span>{publicText("Tomato")}</span>
                  <span>{publicText("Wet / Dry")}</span>
                  <span>{publicText("Median aperture")}</span>
                  <span>{publicText("Control / A / B")}</span>
                  <span>{publicText("Closed = 0 \xB5m")}</span>
                </div>

                <div className="science-case-hero__headline-result is-four case02-hero__facts">
                  <div>
                    <small>{publicText("CONDITIONS")}</small>
                    <strong>{publicText("3")}</strong>
                  </div>
                  <div>
                    <small>{publicText("IMAGE SETS")}</small>
                    <strong>{publicText(publicImages)}</strong>
                  </div>
                  <div>
                    <small>{publicText("DETECTED STOMATA")}</small>
                    <strong>{publicText(publicStomata.toLocaleString("ja-JP"))}</strong>
                  </div>
                  <div>
                    <small>{publicText("POINT RECORD")}</small>
                    <strong>{publicText(CASE02.environmentalRecord.value.toFixed(1))}{publicText("\xB0C")}</strong>
                  </div>
                </div>
              </Reveal>

              <Reveal className="science-case-hero__media case02-hero__media" delay={120}>
                <div className="case02-hero-split">
                  <figure>
                    <img src={CASE02.images.rewaterPlant} alt={publicText("7月16日11時30分頃の湿潤条件")} />
                    <figcaption>
                      <span>{publicText("WET / 7.16 11:30")}</span>
                      <strong>{publicText("B\u5264 5.40 \xB5m")}</strong>
                    </figcaption>
                  </figure>
                  <figure>
                    <img src={CASE02.images.relatedPlant} alt={publicText("7月14日11時頃の培地乾燥条件")} />
                    <figcaption>
                      <span>{publicText("DRY / 7.14 11:00")}</span>
                      <strong>{publicText("B\u5264 2.86 \xB5m")}</strong>
                    </figcaption>
                  </figure>
                </div>

                <div className="case02-hero__finding case02-hero__finding--strategy">
                  <RadarChartOutlined />
                  <div>
                    <small>{publicText("B\u5264 / MEDIAN APERTURE")}</small>
                    <strong>{publicText("5.40 \u2192 2.86 \xB5m")}</strong>
                    <span>{publicText("\u6E7F\u6F64\u6761\u4EF6 \u2192 \u5225\u65E5\u306E\u4E7E\u71E5\u30FB\u840E\u308C\u6761\u4EF6")}</span>
                  </div>
                </div>

                <div className="case02-hero__temperature">
                  <FireOutlined />
                  <div>
                    <small>{publicText("11:23\u9803\u306E\u6E29\u5EA6\u8A08\u8868\u793A")}</small>
                    <strong>{publicText("45.1\xB0C")}</strong>
                    <span>{publicText("\u5358\u4E00\u70B9\u306E\u5199\u771F\u8A18\u9332")}</span>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </header>

        <section className="science-section science-question-section">
          <div className="study-content">
            <SectionHeader index="01" eyebrow="STUDY QUESTION" title={publicText("気孔は、常に大きく開いていれば良いのか。")} description={publicText("気孔開口はCO₂取り込みや蒸散冷却につながる一方、乾燥時には水分損失を増やします。本事例では、開度の大小ではなく、水分条件に応じた切り替え方に注目しました。")} />

            <KeyFinding title={publicText("注目したのは「どれだけ開いたか」ではなく、「いつ開き、いつ抑えたか」。")}>
              <p>{publicText("7\u670816\u65E56:30\u306E\u6E7F\u6F64\u6761\u4EF6\u3067\u306F\u30013\u533A\u306E\u6C17\u5B54\u958B\u5EA6\u4E2D\u592E\u5024\u306F2.82\u301C2.86 \xB5m\u3067\u307B\u307C\u540C\u3058\u3067\u3057\u305F\u3002 \u540C\u65E511:30\u306E\u518D\u704C\u6C34\u5F8C\u30FB\u9AD8\u6E29\u6761\u4EF6\u3067\u306F\u3001A\u52644.24 \xB5m\u3001B\u52645.40 \xB5m\u307E\u3067\u958B\u5EA6\u304C\u62E1\u5927\u3057\u307E\u3057\u305F\u3002 \u4E00\u65B9\u3001\u5225\u65E5\u306E\u57F9\u5730\u4E7E\u71E5\u30FB\u840E\u308C\u6761\u4EF6\u3067\u306F\u3001A\u52644.50 \xB5m\u306B\u5BFE\u3057B\u52642.86 \xB5m\u3068\u306A\u308A\u3001 B\u5264\u306F\u9589\u9396\u65B9\u5411\u306E\u5FDC\u7B54\u3092\u793A\u3057\u307E\u3057\u305F\u3002")}</p>
            </KeyFinding>
          </div>
        </section>

        <section className="science-section is-soft case02-design-section">
          <div className="case-container">
            <SectionHeader index="02" eyebrow="EXPERIMENTAL CONDITIONS" title={publicText("水分状態の異なる条件で、気孔応答を比較した。")} description={publicText("水分が十分に利用できる条件と、培地乾燥により植物体に萎れがみられた条件で、Control・A剤・B剤の気孔応答を比較しました。気孔が開いている割合だけでなく、個々の気孔の開度を測定し、水分状態の違いによって各処理区の応答がどのように変化するかを観察しました。")} />

            <ConditionDayCards />

            <MethodGrid items={[{
            label: "処理区",
            value: "3",
            note: "Control / A剤 / B剤"
          }, {
            label: "測定条件",
            value: "3",
            note: "湿潤朝 / 再灌水後・高温 / 乾燥・萎れ"
          }, {
            label: "解析画像",
            value: publicTemplate(367, [publicImages]),
            note: "条件・処理区ごとに画像数は異なる"
          }, {
            label: "検出気孔",
            value: publicStomata.toLocaleString("ja-JP"),
            note: "Closedは開度0 µmとして集計"
          }]} />
          </div>
        </section>

        <ConditionSection condition={wetMorning} index="03" eyebrow="WET CONDITION / STARTING POINT" title={publicText("十分な水分条件では、3区の気孔開度はほぼ同じだった。")} description={publicText("約4:00の十分な灌水後。Open率には72.6〜81.6%の幅がありましたが、気孔開度中央値は2.82〜2.86 µmに収まりました。")} rootState="約4:00の十分灌水後" interpretationTitle="中央値を見ると、3区はほぼ同じスタート地点にいた。">
          <p>{publicText("Control 2.85 \xB5m\u3001A\u52642.82 \xB5m\u3001B\u52642.86 \xB5m\u3067\u3001Kruskal\u2013Wallis\u691C\u5B9A\u3082p=0.4511\u3067\u3057\u305F\u3002 \u3053\u306E\u6642\u70B9\u3067\u306F\u3001\u51E6\u7406\u533A\u306B\u3088\u308B\u660E\u77AD\u306A\u958B\u5EA6\u5206\u5E03\u5DEE\u306F\u78BA\u8A8D\u3055\u308C\u307E\u305B\u3093\u3067\u3057\u305F\u3002")}</p>
        </ConditionSection>

        <ConditionSection condition={wetLate} index="04" eyebrow="WET CONDITION / POST-IRRIGATION × HIGH TEMPERATURE" title={publicText("再灌水後の高温条件で、A剤・B剤は開度を拡大した。")} description={publicText("約10:00の再灌水から約1時間半後。11:23頃の温度計表示は45.1°Cで、11:30頃の中央値はControl 3.42、A剤4.24、B剤5.40 µmでした。")} rootState="約10:00の再灌水後" interpretationTitle="水分が利用できる高温条件では、A剤・B剤で大きな気孔開度が観察された。">
          <p>{publicText("\u89B3\u5BDF\u5024\u3067\u306FA\u5264\u30FBB\u5264\u304CControl\u3088\u308A\u5927\u304D\u306A\u4E2D\u592E\u5024\u3092\u793A\u3057\u307E\u3057\u305F\u3002 \u3053\u306E\u958B\u5EA6\u62E1\u5927\u306F\u3001\u30AC\u30B9\u4EA4\u63DB\u3084\u84B8\u6563\u51B7\u5374\u3092\u9AD8\u3081\u308B\u65B9\u5411\u306E\u5FDC\u7B54\u3068\u6574\u5408\u3057\u307E\u3059\u3002 \u305F\u3060\u3057\u3001\u672C\u8A66\u9A13\u3067\u306F\u51E6\u7406\u533A\u5225\u306E\u8449\u6E29\u3001\u84B8\u6563\u901F\u5EA6\u3001\u6C17\u5B54\u30B3\u30F3\u30C0\u30AF\u30BF\u30F3\u30B9\u3001\u5149\u5408\u6210\u901F\u5EA6\u3092\u540C\u6642\u6E2C\u5B9A\u3057\u3066\u3044\u306A\u3044\u305F\u3081\u3001 \u5B9F\u969B\u306B\u8449\u6E29\u4F4E\u4E0B\u3084\u5149\u5408\u6210\u7DAD\u6301\u3078\u3064\u306A\u304C\u3063\u305F\u3053\u3068\u307E\u3067\u306F\u78BA\u8A8D\u3057\u3066\u3044\u307E\u305B\u3093\u3002")}</p>
        </ConditionSection>

        <ConditionSection condition={dry} index="05" eyebrow="DRY CONDITION / WILTING OBSERVED" title={publicText("培地乾燥・萎れ条件では、B剤が閉鎖方向へ切り替わった。")} description={publicText("7月14日は約4:00の灌水後に追加灌水を行わず、10:58には培地乾燥と萎れを確認。11:00頃の中央値はControl 3.99、A剤4.50、B剤2.86 µmでした。")} rootState="無灌水 / 培地乾燥 / 萎れ" interpretationTitle="B剤では、水分損失を抑える方向と解釈し得る気孔閉鎖が観察された。">
          <p>{publicText("B\u5264\u306FOpen\u738767.3%\u3001\u4E2D\u592E\u50242.86 \xB5m\u3067\u3001Control 91.9%\u30FB3.99 \xB5m\u3001A\u526496.2%\u30FB4.50 \xB5m\u3088\u308A\u9589\u9396\u65B9\u5411\u3067\u3057\u305F\u3002 \u6C17\u5B54\u3092\u9589\u3058\u308B\u3053\u3068\u306F\u4E00\u822C\u306B\u84B8\u6563\u306B\u3088\u308B\u6C34\u5206\u640D\u5931\u3092\u6291\u3048\u308B\u65B9\u5411\u306B\u50CD\u304F\u305F\u3081\u3001 B\u5264\u304C\u4E7E\u71E5\u6761\u4EF6\u306B\u5FDC\u3058\u3066\u9632\u5FA1\u5074\u3078\u5207\u308A\u66FF\u308F\u3063\u305F\u53EF\u80FD\u6027\u304C\u3042\u308A\u307E\u3059\u3002 \u4E00\u65B9\u3001A\u5264\u3067\u306F\u4E7E\u71E5\u30FB\u840E\u308C\u6761\u4EF6\u3067\u3082\u6BD4\u8F03\u7684\u5927\u304D\u306A\u958B\u5EA6\u304C\u89B3\u5BDF\u3055\u308C\u307E\u3057\u305F\u3002 \u3053\u306E\u958B\u53E3\u7DAD\u6301\u304C\u30AC\u30B9\u4EA4\u63DB\u306E\u7DAD\u6301\u3092\u793A\u3059\u306E\u304B\u3001\u6C34\u5206\u640D\u5931\u306E\u5897\u52A0\u3092\u793A\u3059\u306E\u304B\u306F\u3001\u6C17\u5B54\u30C7\u30FC\u30BF\u3060\u3051\u3067\u306F\u5224\u5B9A\u3067\u304D\u307E\u305B\u3093\u3002")}</p>
        </ConditionSection>

        <section className="science-section case02-profile-section">
          <div className="case-container">
            <SectionHeader index="06" eyebrow="RESPONSE PROFILE" title={publicText("3つの条件を並べると、処理区ごとの応答パターンが見えてくる。")} description={publicText("7/14と7/16は別日のため、数値を直接の反復として統計比較するものではありません。ここでは観察された中央値を記述的に並べています。")} />

            <Reveal className="case02-profile-grid">
              <ResponseProfile treatment={{
              key: "control",
              label: "Control"
            }} values={profileCards.find(item => item.key === "control").values} note={publicText("湿潤・乾燥の双方で開度は変化しましたが、今回の3条件だけから一貫した水分応答戦略を特定することはできません。")} />

              <ResponseProfile treatment={{
              key: "a",
              label: "A剤"
            }} values={profileCards.find(item => item.key === "a").values} note={publicText("11:30の再灌水後・高温条件と7/14の乾燥・萎れ条件の双方で、比較的大きな開度が観察されました。この応答の生理的意味は、気孔データだけでは特定できません。")} />

              <ResponseProfile treatment={{
              key: "b",
              label: "B剤"
            }} values={profileCards.find(item => item.key === "b").values} note={publicText("水分が利用できる条件では5.40 µmまで開き、乾燥・萎れ条件では2.86 µmへ抑えられました。条件に応じた切り替えが最も明瞭でした。")} highlight />
            </Reveal>

            <ApertureLegend />

            <KeyFinding title={publicText("A剤とB剤では、水分条件に対する気孔の応答パターンが異なった。")}>
              <p>{publicText("A\u5264\u3067\u306F\u6E7F\u6F64\u30FB\u4E7E\u71E5\u306E\u53CC\u65B9\u3067\u6BD4\u8F03\u7684\u5927\u304D\u306A\u958B\u5EA6\u304C\u89B3\u5BDF\u3055\u308C\u307E\u3057\u305F\u3002 B\u5264\u3067\u306F\u6E7F\u6F64\u6642\u306B\u958B\u5EA6\u3092\u62E1\u5927\u3057\u3001\u4E7E\u71E5\u30FB\u840E\u308C\u6642\u306B\u306F\u958B\u5EA6\u3092\u6291\u3048\u308B\u65B9\u5411\u3078\u5207\u308A\u66FF\u308F\u308A\u307E\u3057\u305F\u3002 \u3053\u308C\u306F\u300C\u5E38\u306B\u958B\u304B\u305B\u308B\u8CC7\u6750\u300D\u3068\u300C\u5E38\u306B\u9589\u3058\u3055\u305B\u308B\u8CC7\u6750\u300D\u3068\u3044\u3046\u5358\u7D14\u306A\u9055\u3044\u3067\u306F\u306A\u304F\u3001 \u6C34\u5206\u6761\u4EF6\u306B\u5BFE\u3059\u308B\u6C17\u5B54\u8ABF\u7BC0\u30D1\u30BF\u30FC\u30F3\u306E\u9055\u3044\u3067\u3042\u308B\u53EF\u80FD\u6027\u304C\u3042\u308A\u307E\u3059\u3002")}</p>
            </KeyFinding>
          </div>
        </section>

        <section className="science-section is-dark case02-literature-section">
          <div className="study-content">
            <SectionHeader index="07" eyebrow="OBSERVATION × RELATED RESEARCH" title={publicText("既往研究で説明できる応答と、次の検証につながる応答が見えてきた。")} description={publicText("B剤で観察された乾燥時の閉鎖方向の応答を既往研究と照合します。一方、気孔開度だけでは生理的意味を特定できない応答は、次の統合計測につながる研究課題です。")} />

            <Reveal className="case02-literature-flow">
              <div>
                <small>{publicText("PUBLISHED RESEARCH")}</small>
                <strong>{publicText("\u4E7E\u71E5\u6642\u306E\u6C17\u5B54\u9589\u9396\u3068\u6C34\u5206\u4FDD\u6301")}</strong>
                <span>{publicText("ABA\u30B7\u30B0\u30CA\u30EB\u3078\u306E\u95A2\u4E0E\u3001\u6C17\u5B54\u9589\u9396\u3001\u84B8\u6563\u4F4E\u4E0B\u3001\u8449\u306E\u6C34\u5206\u4FDD\u6301\u3092\u5831\u544A")}</span>
              </div>
              <b>{publicText("\u2192")}</b>
              <div>
                <small>{publicText("OUR OBSERVATION")}</small>
                <strong>{publicText("B\u5264\uFF1A\u4E7E\u71E5\u6642 2.86 \xB5m")}</strong>
                <span>{publicText("Open\u738767.3% \uFF0F Open 245 \uFF0F Closed 119 \uFF0F 364\u6C17\u5B54")}</span>
              </div>
              <b>{publicText("\u2192")}</b>
              <div>
                <small>{publicText("NEXT VALIDATION")}</small>
                <strong>{publicText("\u6C34\u5206\u4FDD\u6301\u306B\u3064\u306A\u304C\u3063\u305F\u304B")}</strong>
                <span>{publicText("\u84B8\u6563\u3001\u8449\u6C34\u30DD\u30C6\u30F3\u30B7\u30E3\u30EB\u3001\u76F8\u5BFE\u542B\u6C34\u7387\u306A\u3069\u3068\u306E\u540C\u6642\u8A08\u6E2C\u304C\u5FC5\u8981")}</span>
              </div>
            </Reveal>

            <DroughtReferenceCard />

            <ScientificNote title={publicText("この結果から、どこまで言える？")} tone="caution">
              <p>{publicText("B\u5264\u3067\u306F\u3001\u4E7E\u71E5\u6761\u4EF6\u3067\u6C17\u5B54\u3092\u9589\u3058\u308B\u65B9\u5411\u306E\u5FDC\u7B54\u304C\u89B3\u5BDF\u3055\u308C\u307E\u3057\u305F\u3002 \u3053\u308C\u306F\u3001\u65E2\u5F80\u7814\u7A76\u3067\u5831\u544A\u3055\u308C\u3066\u3044\u308B\u4E7E\u71E5\u6642\u306E\u6C17\u5B54\u9589\u9396\u3068\u6C34\u5206\u4FDD\u6301\u306B\u6574\u5408\u3059\u308B\u73FE\u8C61\u3067\u3059\u3002")}</p>
              <p>{publicText("\u4E00\u65B9\u3001\u672C\u8A66\u9A13\u3067\u76F4\u63A5\u78BA\u8A8D\u3057\u305F\u306E\u306F\u6C17\u5B54\u306E\u72B6\u614B\u3067\u3059\u3002\u5B9F\u969B\u306B\u84B8\u6563\u91CF\u304C\u6E1B\u5C11\u3057\u305F\u304B\u3001 \u6C34\u5206\u4FDD\u6301\u3084\u4E7E\u71E5\u8010\u6027\u304C\u5411\u4E0A\u3057\u305F\u304B\u3001\u307E\u305F\u5F15\u7528\u8AD6\u6587\u3068\u540C\u3058\u4F5C\u7528\u6A5F\u5E8F\u3067\u751F\u3058\u305F\u304B\u306B\u3064\u3044\u3066\u306F\u3001 \u4ECA\u5F8C\u306E\u691C\u8A3C\u304C\u5FC5\u8981\u3067\u3059\u3002")}</p>
            </ScientificNote>

            <Reveal className="case02-open-question-grid">
              <article>
                <span>{publicText("OPEN QUESTION / A")}</span>
                <h3>{publicText("\u4E7E\u71E5\u30FB\u840E\u308C\u6761\u4EF6\u3067\u3082 4.50 \xB5m")}</h3>
                <p>{publicText("A\u5264\u3067\u306F\u4E7E\u71E5\u6761\u4EF6\u3067\u3082\u6BD4\u8F03\u7684\u5927\u304D\u306A\u6C17\u5B54\u958B\u5EA6\u304C\u89B3\u5BDF\u3055\u308C\u307E\u3057\u305F\u3002 \u30AC\u30B9\u4EA4\u63DB\u3092\u7DAD\u6301\u3057\u3066\u3044\u305F\u306E\u304B\u3001\u6C34\u5206\u640D\u5931\u304C\u5897\u3048\u3066\u3044\u305F\u306E\u304B\u306F\u3001\u6C17\u5B54\u3060\u3051\u3067\u306F\u533A\u5225\u3067\u304D\u307E\u305B\u3093\u3002")}</p>
              </article>
              <article>
                <span>{publicText("OPEN QUESTION / B")}</span>
                <h3>{publicText("\u9589\u9396\u65B9\u5411\u306E\u5FDC\u7B54\u306F\u3001\u9632\u5FA1\u306B\u3064\u306A\u304C\u3063\u305F\u304B")}</h3>
                <p>{publicText("B\u5264\u306E\u9589\u9396\u65B9\u5411\u3078\u306E\u5909\u5316\u304C\u3001\u5B9F\u969B\u306B\u8449\u306E\u6C34\u5206\u4FDD\u6301\u3084\u518D\u704C\u6C34\u5F8C\u306E\u56DE\u5FA9\u306B\u5BC4\u4E0E\u3057\u305F\u304B\u3092 \u691C\u8A3C\u3059\u308B\u3053\u3068\u304C\u3001\u6B21\u306E\u7814\u7A76\u8AB2\u984C\u3067\u3059\u3002")}</p>
              </article>
            </Reveal>
          </div>
        </section>

        <section className="science-section is-soft case02-peer-review-section">
          <div className="study-content">
            <SectionHeader index="08" eyebrow="STOMATASCOPE IN PEER-REVIEWED RESEARCH" title={publicText("StomataScopeによる気孔研究は、観察から統合生理解析へ。")} description={publicText("査読研究では、StomataScopeによる気孔開度と、気孔コンダクタンス、蒸散、葉温、光合成関連指標を同じ時間軸で組み合わせた解析が行われています。")} />

            <Reveal className="case02-peer-review-grid">
              <article>
                <small>{publicText("MEASURED TOGETHER")}</small>
                <strong>{publicText("\u6C17\u5B54\u958B\u5EA6\uFF0B\u751F\u7406\u6307\u6A19")}</strong>
                <span>{publicText("\u6C17\u5B54\u30B3\u30F3\u30C0\u30AF\u30BF\u30F3\u30B9\u3001\u84B8\u6563\u901F\u5EA6\u3001\u8449\u6E29\u3001PSII\u91CF\u5B50\u52B9\u7387\u3092\u7D71\u5408")}</span>
              </article>
              <article>
                <small>{publicText("OBSERVED UNDER HEAT")}</small>
                <strong>{publicText("\u958B\u5EA6\u30FB\u84B8\u6563\u306F\u5897\u52A0")}</strong>
                <span>{publicText("\u9AD8\u6E29\u533A\u3067\u306F\u5348\u5F8C\u306E\u6C17\u5B54\u958B\u5EA6\u3001\u6C17\u5B54\u30B3\u30F3\u30C0\u30AF\u30BF\u30F3\u30B9\u3001\u84B8\u6563\u304C\u5897\u52A0")}</span>
              </article>
              <article>
                <small>{publicText("NOT A SIMPLE BENEFIT")}</small>
                <strong>{publicText("\u4E7E\u7269\u751F\u7523\u306F\u4F4E\u4E0B")}</strong>
                <span>{publicText("\u6C17\u5B54\u304C\u5927\u304D\u304F\u958B\u3044\u3066\u3082\u3001\u5149\u5408\u6210\u7CFB\u3084\u751F\u80B2\u304C\u826F\u597D\u3068\u306F\u9650\u3089\u306A\u304B\u3063\u305F")}</span>
              </article>
            </Reveal>

            <PeerReviewedStudyCard />

            <KeyFinding title={publicText("気孔の値は、答えではなく、次に測るべきものを示す。")}>
              <p>{publicText("\u672CCase Study\u3067\u306F\u3001\u65E2\u5F80\u7814\u7A76\u304B\u3089\u8003\u5BDF\u3067\u304D\u308B\u5FDC\u7B54\u3068\u3001\u6C17\u5B54\u30C7\u30FC\u30BF\u3060\u3051\u3067\u306F\u751F\u7406\u7684\u610F\u5473\u3092 \u7279\u5B9A\u3067\u304D\u306A\u3044\u5FDC\u7B54\u306E\u4E21\u65B9\u304C\u89B3\u5BDF\u3055\u308C\u307E\u3057\u305F\u3002\u3053\u3046\u3057\u305F\u5DEE\u3092\u5B9A\u91CF\u5316\u3059\u308B\u3053\u3068\u3067\u3001 \u5149\u5408\u6210\u3001\u84B8\u6563\u3001\u8449\u6E29\u3001\u6C34\u5206\u72B6\u614B\u306A\u3069\u3001\u6B21\u306E\u691C\u8A3C\u9805\u76EE\u3092\u5177\u4F53\u7684\u306B\u8A2D\u8A08\u3067\u304D\u307E\u3059\u3002")}</p>
            </KeyFinding>
          </div>
        </section>

        <section className="science-section is-soft">
          <div className="study-content">
            <SectionHeader index="09" eyebrow="STATISTICAL INTERPRETATION" title={publicText("観察結果と、証明できる範囲を分ける。")} />

            <MethodGrid items={[{
            label: "7/16 06:30 湿潤",
            value: "p = 0.4511",
            note: "3群の分布差は認められず"
          }, {
            label: "7/16 11:30 湿潤",
            value: "p < 0.0001",
            note: "少なくとも1群の分布が異なる"
          }, {
            label: "7/14 11:00 乾燥",
            value: "p < 0.0001",
            note: "少なくとも1群の分布が異なる"
          }]} />

            <ScientificNote title={publicText("各時点のp値だけでは、どの2群が異なるかは分からない")} tone="caution">
              <p>{publicText("Kruskal\u2013Wallis\u691C\u5B9A\u306F\u30013\u7FA4\u306E\u5C11\u306A\u304F\u3068\u3082\u3044\u305A\u308C\u304B\u304C\u7570\u306A\u308B\u304B\u3092\u793A\u3057\u307E\u3059\u3002 Control\u5BFEA\u5264\u3001Control\u5BFEB\u5264\u3001A\u5264\u5BFEB\u5264\u306E\u500B\u5225\u5DEE\u3092\u5224\u65AD\u3059\u308B\u306B\u306F\u3001\u9069\u5207\u306A\u591A\u91CD\u6BD4\u8F03\u304C\u5FC5\u8981\u3067\u3059\u3002")}</p>
            </ScientificNote>

            <ScientificNote title={publicText("7/14と7/16は直接比較できる反復試験ではない")} tone="info">
              <p>{publicText("\u4E21\u65E5\u306F\u4E7E\u71E5\u5F37\u5EA6\u3001\u6C17\u6E29\u3001VPD\u3001\u8449\u4F4D\u3001\u690D\u7269\u4F53\u6C34\u5206\u72B6\u614B\u3001\u51E6\u7406\u5F8C\u7D4C\u904E\u6642\u9593\u304C\u7D71\u4E00\u3055\u308C\u305F\u8A66\u9A13\u3067\u306F\u3042\u308A\u307E\u305B\u3093\u3002 \u307E\u305F\u3001\u691C\u51FA\u3055\u308C\u305F\u500B\u3005\u306E\u6C17\u5B54\u306F\u72EC\u7ACB\u3057\u305F\u751F\u7269\u5B66\u7684\u53CD\u5FA9\u3067\u306F\u3042\u308A\u307E\u305B\u3093\u3002 \u5FDC\u7B54\u6226\u7565\u3092\u6B63\u5F0F\u306B\u691C\u8A3C\u3059\u308B\u306B\u306F\u3001\u72EC\u7ACB\u500B\u4F53\u306E\u53CD\u5FA9\u3001\u57F9\u5730\u542B\u6C34\u7387\u3001\u8449\u6E29\u3001\u84B8\u6563\u3001\u6C17\u5B54\u30B3\u30F3\u30C0\u30AF\u30BF\u30F3\u30B9\u3001 \u5149\u5408\u6210\u901F\u5EA6\u3001\u8449\u6C34\u30DD\u30C6\u30F3\u30B7\u30E3\u30EB\u7B49\u306E\u540C\u6642\u8A08\u6E2C\u304C\u5FC5\u8981\u3067\u3059\u3002")}</p>
            </ScientificNote>
          </div>
        </section>

        <section className="science-case-conclusion case02-conclusion case02-conclusion--strategy">
          <div className="study-content">
            <Reveal>
              <LineChartOutlined />
              <span>{publicText("MEASURE THE RESPONSE. FIND THE NEXT QUESTION.")}</span>
              <h2>{publicText("\u52B9\u679C\u3092\u898B\u308B\u306A\u3089\u3001")}<br />{publicText("\u300C\u5927\u304D\u304F\u958B\u3044\u305F\u304B\u300D\u3067\u306F\u306A\u304F\u3001\u300C\u74B0\u5883\u306B\u3069\u3046\u5FDC\u7B54\u3057\u305F\u304B\u300D\u3092\u6E2C\u308B\u3002")}</h2>
              <p>{publicText("\u672C\u4E8B\u4F8B\u3067\u306F\u3001\u5341\u5206\u306A\u6C34\u5206\u6761\u4EF6\u3067\u306F3\u533A\u306E\u958B\u59CB\u70B9\u304C\u8FD1\u304F\u3001 \u518D\u704C\u6C34\u5F8C\u306E\u9AD8\u6E29\u6761\u4EF6\u3068\u3001\u5225\u65E5\u306E\u4E7E\u71E5\u30FB\u840E\u308C\u6761\u4EF6\u3067\u7570\u306A\u308B\u5FDC\u7B54\u30D1\u30BF\u30FC\u30F3\u304C\u89B3\u5BDF\u3055\u308C\u307E\u3057\u305F\u3002 B\u5264\u3067\u306F\u65E2\u5F80\u7814\u7A76\u3068\u6574\u5408\u3059\u308B\u9589\u9396\u65B9\u5411\u306E\u5FDC\u7B54\u304C\u898B\u3089\u308C\u3001A\u5264\u3067\u306F\u8FFD\u52A0\u691C\u8A3C\u306B\u3064\u306A\u304C\u308B \u7279\u5FB4\u7684\u306A\u958B\u5EA6\u304C\u6349\u3048\u3089\u308C\u307E\u3057\u305F\u3002StomataScope\u306F\u3001\u6C17\u5B54\u3060\u3051\u3067\u52B9\u679C\u3092\u65AD\u5B9A\u3059\u308B\u306E\u3067\u306F\u306A\u304F\u3001 \u690D\u7269\u306E\u5FDC\u7B54\u3092\u5B9A\u91CF\u5316\u3057\u3001\u6B21\u306E\u7814\u7A76\u4EEE\u8AAC\u3068\u6E2C\u5B9A\u8A2D\u8A08\u306B\u3064\u306A\u3052\u308B\u305F\u3081\u306E\u8A08\u6E2C\u30C4\u30FC\u30EB\u3067\u3059\u3002")}</p>
            </Reveal>
          </div>
        </section>

        <div className="case-container">
          <CaseNavigation previous={{
          id: "01",
          path: "/case-studies/01",
          title: "気孔状態と、その後の水分応答"
        }} next={{
          id: "03",
          path: "/case-studies/03",
          title: "商業栽培トマト圃場での大量計測"
        }} />
        </div>
      </article>
    </PublicLayout>;
}
export default CaseStudy02;
